/**
 * @fileoverview Template Upload Modal with SVG field extraction
 */
import { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { Upload, FileText, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export const TemplateUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
    const { t } = useLanguage();
    const [file, setFile] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('custom');
    const [extractedFields, setExtractedFields] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);

    const handleFileChange = async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        // Validate file type
        if (!selectedFile.name.endsWith('.svg')) {
            setError(t('templates.svg_only', 'Solo se permiten archivos SVG'));
            return;
        }

        // Validate file size (5MB)
        if (selectedFile.size > 5 * 1024 * 1024) {
            setError(t('templates.file_too_large', 'El archivo es demasiado grande. Máximo 5MB'));
            return;
        }

        setFile(selectedFile);
        setError(null);

        // Auto-fill name from filename
        if (!name) {
            const fileName = selectedFile.name.replace('.svg', '').replace(/_/g, ' ');
            setName(fileName);
        }

        // Read SVG content and extract fields
        try {
            const text = await selectedFile.text();
            const fields = extractFieldsFromSVG(text);
            setExtractedFields(fields);
        } catch (err) {
            console.error('Error reading SVG:', err);
        }
    };

    const extractFieldsFromSVG = (svgContent) => {
        const fieldPattern = /\{\{(\w+)\}\}/g;
        const matches = [...svgContent.matchAll(fieldPattern)];
        const uniqueFields = new Set();
        const fields = [];

        matches.forEach(match => {
            const fieldName = match[1];
            if (!uniqueFields.has(fieldName)) {
                uniqueFields.add(fieldName);

                // Auto-detect field type
                let type = 'text';
                const lowerName = fieldName.toLowerCase();

                if (lowerName.includes('date') || lowerName.includes('fecha')) {
                    type = 'date';
                } else if (lowerName.includes('email') || lowerName.includes('correo')) {
                    type = 'email';
                } else if (lowerName.includes('hours') || lowerName.includes('horas') ||
                    lowerName.includes('score') || lowerName.includes('number')) {
                    type = 'number';
                }

                // Convert camelCase to Title Case
                const label = fieldName
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())
                    .trim();

                fields.push({ name: fieldName, label, type });
            }
        });

        return fields;
    };

    const handleUpload = async () => {
        if (!file || !name) {
            setError(t('templates.fill_required', 'Por favor complete todos los campos requeridos'));
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('template', file);
            formData.append('name', name);
            formData.append('description', description);
            formData.append('category', category);

            const response = await fetch('/api/templates/upload', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                },
                body: formData
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || t('templates.upload_error', 'Error al subir la plantilla'));
            }

            const data = await response.json();

            // Reset form
            setFile(null);
            setName('');
            setDescription('');
            setCategory('custom');
            setExtractedFields([]);

            onUploadSuccess?.(data.template);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        if (!isUploading) {
            setFile(null);
            setName('');
            setDescription('');
            setCategory('custom');
            setExtractedFields([]);
            setError(null);
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <div className="p-6">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">
                    {t('templates.upload_title', 'Nueva Plantilla')}
                </h2>

                {/* File Upload */}
                <div className="mb-6">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                        {t('templates.svg_file', 'Archivo SVG')} <span className="text-red-500">*</span>
                    </label>
                    <div
                        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${file ? 'border-blue-500 bg-blue-50/30' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
                            }`}
                        onClick={() => document.getElementById('svg-upload')?.click()}
                    >
                        <input
                            type="file"
                            accept=".svg"
                            onChange={handleFileChange}
                            id="svg-upload"
                            style={{ display: 'none' }}
                        />

                        {file ? (
                            <div className="flex flex-col items-center">
                                <FileText className="w-12 h-12 text-blue-500 mb-3" />
                                <p className="font-medium text-slate-800">{file.name}</p>
                                <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                                {extractedFields.length > 0 && (
                                    <div className="mt-3 flex items-center gap-2 text-green-600">
                                        <CheckCircle2 className="w-4 h-4" />
                                        <span className="text-sm font-medium">{extractedFields.length} {t('templates.fields_detected', 'campos detectados')}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center">
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                                    <Upload className="w-8 h-8 text-slate-400" />
                                </div>
                                <p className="font-medium text-slate-700 mb-1">{t('templates.click_upload', 'Haz clic para subir tu SVG')}</p>
                                <p className="text-sm text-slate-500">{t('templates.max_size', 'Máximo 5MB')}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Template Details */}
                <div className="space-y-4 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            {t('templates.template_name', 'Nombre de la Plantilla')} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('templates.name_placeholder', 'Ej. Certificado de Finalización')}
                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            {t('templates.description', 'Descripción')}
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('templates.desc_placeholder', 'Describe el propósito de esta plantilla...')}
                            rows={3}
                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            {t('templates.category', 'Categoría')}
                        </label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                        >
                            <option value="custom">{t('templates.category_custom', 'Personalizado')}</option>
                            <option value="training">{t('templates.category_training', 'Capacitación')}</option>
                            <option value="mro">{t('templates.category_mro', 'Mantenimiento')}</option>
                        </select>
                    </div>
                </div>

                {/* Extracted Fields Preview */}
                {extractedFields.length > 0 && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            {t('templates.fields_auto_detected', 'Campos Detectados Automáticamente')}
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                            {extractedFields.map((field, idx) => (
                                <div key={idx} className="text-xs bg-white p-2 rounded border border-blue-100">
                                    <span className="font-mono text-blue-600">{`{{${field.name}}}`}</span>
                                    <span className="text-slate-500 ml-2">({field.type})</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={handleClose}
                        disabled={isUploading}
                        className="flex-1 py-3 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {t('common.cancel', 'Cancelar')}
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={isUploading || !file || !name}
                        className="flex-1 py-3 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isUploading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                {t('templates.uploading', 'Subiendo...')}
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4" />
                                {t('templates.upload_template', 'Subir Plantilla')}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
